import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { BasketProvider } from "@/lib/basket";
import { api } from "@/lib/api";
import { initTheme, getTheme, onTheme } from "@/lib/theme";
import AdminInbox from "@/pages/AdminInbox";
import UploadBOQ from "@/pages/UploadBOQ";
import Portal from "@/pages/Portal";
import CategoryPage from "@/pages/CategoryPage";
import FamilyPage from "@/pages/FamilyPage";
import CableCutting from "@/pages/CableCutting";
import PriceList from "@/pages/PriceList";
import EstimateBar from "@/components/EstimateBar";
import { EstimateProvider } from "@/lib/estimate";
import Nav from "@/components/Nav";
import Seo from "@/components/Seo";
import LocationTrust from "@/components/LocationTrust";
import { FAQS } from "@/components/FAQSection";

const FAQ_JSONLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};
import Hero from "@/components/Hero";
import LiveRatesStrip from "@/components/LiveRatesStrip";
import Marquee from "@/components/Marquee";
import Categories from "@/components/Categories";
import CrossSection from "@/components/CrossSection";
import HTCompare from "@/components/HTCompare";
import Products from "@/components/Products";
import PriceLists from "@/components/PriceLists";
import CableCalculator from "@/components/CableCalculator";
import Stats from "@/components/Stats";
import ClientsIndustries from "@/components/ClientsIndustries";
import BrandWall from "@/components/BrandWall";
import About from "@/components/About";
import FAQSection from "@/components/FAQSection";
import Enquiry from "@/components/Enquiry";
import QuoteBasket from "@/components/QuoteBasket";
import WhatsAppFloating from "@/components/WhatsAppFloating";
import RoxyAI from "@/components/RoxyAI";
import Footer from "@/components/Footer";

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <div className="min-h-screen bg-paper text-ink flex items-center justify-center p-8">
                    <p className="font-mono text-sm text-mist">Something tripped a fuse — please reload the page.</p>
                </div>
            );
        }
        return this.props.children;
    }
}

function Site() {
    return (
        <>
            <Seo />
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
            </Helmet>
            <Nav />
            <main>
                <Hero />
                <LiveRatesStrip />
                <Marquee />
                <Categories />
                <CrossSection />
                <HTCompare />
                <Products />
                <PriceLists />
                <CableCalculator />
                <Stats />
                <ClientsIndustries />
                <BrandWall />
                <About />
                <FAQSection />
                <Enquiry />
                <LocationTrust />
            </main>
            <Footer />
            <QuoteBasket />
            <WhatsAppFloating />
        </>
    );
}

function AuthCallback() {
    const navigate = useNavigate();
    const processed = useRef(false);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        const hash = window.location.hash;
        const sid = hash.split("session_id=")[1]?.split("&")[0];
        api.post("/auth/session", { session_id: sid })
            .then((res) => {
                window.history.replaceState(null, "", "/portal");
                navigate("/portal", { state: { user: res.data } });
            })
            .catch(() => navigate("/portal"));
    }, [navigate]);

    return (
        <div className="min-h-screen bg-paper flex items-center justify-center">
            <p className="font-mono text-sm text-mist">Signing you in…</p>
        </div>
    );
}

function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/admin" element={<AdminInbox />} />
            <Route path="/upload-boq" element={<UploadBOQ />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/family/:slug" element={<FamilyPage />} />
            <Route path="/category/:slug" element={<CategoryPage />} />
            <Route path="/cutting" element={<CableCutting />} />
            <Route path="/prices" element={<PriceList />} />
            <Route path="*" element={<Site />} />
        </Routes>
    );
}

function App() {
    const [theme, setTheme] = useState(getTheme());

    useEffect(() => {
        initTheme();
        return onTheme(setTheme);
    }, []);

    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (t) => {
            lenis.raf(t);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
            window.__lenis = null;
        };
    }, []);

    return (
        <div className="grain bg-paper text-ink min-h-screen">
            <BrowserRouter>
                <HelmetProvider>
                    <ErrorBoundary>
                        <EstimateProvider>
                            <BasketProvider>
                                <AppRouter />
                                <RoxyAI />
                                <EstimateBar />
                            </BasketProvider>
                        </EstimateProvider>
                        <Toaster theme={theme} position="bottom-right" richColors />
                    </ErrorBoundary>
                </HelmetProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
